import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import DashboardShell from '../../components/DashboardShell';
import AdminPageGuide from '../../components/admin/AdminPageGuide';
import SubscriptionGate from '../../components/subscription/SubscriptionGate';
import SchoolVerificationGate from '../../components/SchoolVerificationGate';
import PendingVerificationPage from './PendingVerificationPage';
import FinanceAdminOnly from '../../components/FinanceAdminOnly';
import LibraryAdminOnly from '../../components/LibraryAdminOnly';
import SideBar from './SideBar';
import AdminProfile from './AdminProfile';
import AppSettingsPage from '../AppSettingsPage';
import AdminHomePage from './AdminHomePage';
import UniversityHomePage from './UniversityHomePage';
import CurrentTermIndicator from '../../components/admin/CurrentTermIndicator';
import AdminHeader from '../../components/admin/AdminHeader';

import AddStudent from './studentRelated/AddStudent';
import BulkStudentUpload from './studentRelated/BulkStudentUpload';
import SeeComplains from './studentRelated/SeeComplains';
import ShowStudents from './studentRelated/ShowStudents';
import ClassStudents from './studentRelated/ClassStudents';
import StudentAttendance from './studentRelated/StudentAttendance';
import StudentExamMarks from './studentRelated/StudentExamMarks';
import ViewStudent from './studentRelated/ViewStudent';
import EnrollmentReview from './studentRelated/EnrollmentReview';
import SchoolRollover from './studentRelated/SchoolRollover';

import AddNotice from './noticeRelated/AddNotice';
import ShowNotices from './noticeRelated/ShowNotices';
import SchoolCalendar from './calendarRelated/SchoolCalendar';
import ClassTimetablePage from './calendarRelated/ClassTimetablePage';
import AdminAcademicCalendarPage from './calendarRelated/AdminAcademicCalendarPage';
import AcademicYearManagement from './calendarRelated/AcademicYearManagement';

import AdminSubjectChangeRequests from './subjectRelated/AdminSubjectChangeRequests';
import SubjectForm from './subjectRelated/SubjectForm';
import ShowSubjects from './subjectRelated/ShowSubjects';
import ViewSubject from './subjectRelated/ViewSubject';

import AddStaff from './teacherRelated/AddStaff';
import SetupHomeroomStaff from './teacherRelated/SetupHomeroomStaff';
import ChooseStaffRole from './teacherRelated/ChooseStaffRole';
import ChooseClass from './teacherRelated/ChooseClass';
import ChooseSubject from './teacherRelated/ChooseSubject';
import ShowTeachers from './teacherRelated/ShowTeachers';
import TeacherDetails from './teacherRelated/TeacherDetails';
import TeacherManagementOverview from './teacherRelated/TeacherManagementOverview';
import SchemeApprovals from './teacherRelated/SchemeApprovals';
import LeaveApprovals from './teacherRelated/LeaveApprovals';
import DisciplinaryApprovals from './studentRelated/DisciplinaryApprovals';
import SubmissionReview from './teacherRelated/SubmissionReview';
import StaffAttendanceRegister from './teacherRelated/StaffAttendanceRegister';

import AddClass from './classRelated/AddClass';
import ClassDetails from './classRelated/ClassDetails';
import ClassResults from './classRelated/ClassResults';
import ExamResultsOverview from './examRelated/ExamResultsOverview';
import StudentReportCard from './examRelated/StudentReportCard';
import ClassResultsReview from './examRelated/ClassResultsReview';
import ResultsRecords from './examRelated/ResultsRecords';
import StudentTranscript from './examRelated/StudentTranscript';
import CurriculumGuide from './examRelated/CurriculumGuide';
import ShowClasses from './classRelated/ShowClasses';

import FeeStructureManagement from './feeRelated/FeeStructureManagement';
import FeeCollection from './feeRelated/FeeCollection';
import FeePaymentSubmissions from './feeRelated/FeePaymentSubmissions';
import SchoolFeePaymentSettings from './feeRelated/SchoolFeePaymentSettings';
import FeeRecordDetail from './feeRelated/FeeRecordDetail';
import FeeDefaulters from './feeRelated/FeeDefaulters';
import FeeAidManagement from './feeRelated/FeeAidManagement';
import FinanceReports from './feeRelated/FinanceReports';
import ExpenseLog from './expenseRelated/ExpenseLog';
import ExpenseDetail from './expenseRelated/ExpenseDetail';
import TermBudgetManagement from './expenseRelated/TermBudgetManagement';
import ExpenseSummary from './expenseRelated/ExpenseSummary';
import SchoolPageBuilder from './schoolPage/SchoolPageBuilder';
import HostelManagement from './hostelRelated/HostelManagement';
import LibraryManagement from './libraryRelated/LibraryManagement';
import AdminTermResourcesOverview from './libraryRelated/AdminTermResourcesOverview';
import InfrastructureManagement from './infrastructureRelated/InfrastructureManagement';
import AdminAnalyticsPage from './analyticsRelated/AdminAnalyticsPage';
import EmisReportsDashboard from './emisRelated/EmisReportsDashboard';
import ManebDashboard from './manebRelated/ManebDashboard';
import CaAssessmentManagement from './examRelated/CaAssessmentManagement';
import ManebSessionDetail from './manebRelated/ManebSessionDetail';
import ManebCertificates from './manebRelated/ManebCertificates';

const AdminDashboard = () => {
    const { currentUser } = useSelector((state) => state.user);
    const adminHomeElement = currentUser?.schoolType === "University" ? <UniversityHomePage /> : <AdminHomePage />;

    return (
        <Routes>
            <Route path="pending-verification" element={<PendingVerificationPage />} />
            <Route
                path="*"
                element={
                    <SchoolVerificationGate>
                        <SubscriptionGate>
                        <DashboardShell
            title="School office"
            sidebar={<SideBar />}
            defaultDesktopOpen
            headerExtra={
                <>
                    <AdminHeader />
                    {currentUser?.schoolType === "University" ? null : <CurrentTermIndicator />}
                </>
            }
        >
            <AdminPageGuide />
            <Routes>
                <Route index element={adminHomeElement} />
                <Route path="dashboard" element={adminHomeElement} />
                <Route path="profile" element={<AdminProfile />} />
                <Route path="app-settings" element={<AppSettingsPage />} />
                <Route path="complains" element={<SeeComplains />} />

                <Route path="addnotice" element={<AddNotice />} />
                <Route path="notices" element={<ShowNotices />} />
                <Route path="calendar" element={<SchoolCalendar />} />
                <Route path="academic-calendar" element={<AdminAcademicCalendarPage />} />
                <Route path="academic-years" element={<AcademicYearManagement />} />
                <Route path="timetables" element={<ClassTimetablePage />} />

                <Route path="subjects" element={<Navigate to="/Admin/subjects/chooseclass" replace />} />
                <Route path="subjects/class/:classID" element={<ShowSubjects />} />
                <Route path="subjects/subject/:classID/:subjectID" element={<ViewSubject />} />
                <Route path="subjects/chooseclass" element={<ChooseClass situation="Subject" />} />
                <Route path="subjects/changes" element={<AdminSubjectChangeRequests />} />
                <Route path="addsubject/:id" element={<SubjectForm />} />
                <Route path="class/subject/:classID/:subjectID" element={<ViewSubject />} />
                <Route path="subject/student/attendance/:studentID/:subjectID" element={<StudentAttendance />} />
                <Route path="subject/student/marks/:studentID/:subjectID" element={<StudentExamMarks situation="Subject" />} />

                <Route path="addclass" element={<AddClass />} />
                <Route path="classes" element={<ShowClasses />} />
                <Route path="classes/class/:id" element={<ClassDetails />} />
                <Route path="classes/results/overview" element={<ExamResultsOverview />} />
                <Route path="classes/results/:id" element={<ClassResults />} />
                <Route path="students/report-card/:studentId" element={<StudentReportCard />} />
                <Route path="students/transcript/:studentId" element={<StudentTranscript />} />
                <Route path="curriculum" element={<CurriculumGuide />} />
                <Route path="class/addstudents/:id" element={<AddStudent situation="Class" />} />
                <Route path="class/:classId/bulk-upload" element={<BulkStudentUpload />} />

                <Route path="addstudents" element={<AddStudent situation="Student" />} />
                <Route path="students/bulk-upload" element={<ChooseClass situation="BulkUpload" />} />
                <Route path="students" element={<ShowStudents />} />
                <Route path="students/chooseclass" element={<ChooseClass situation="Student" />} />
                <Route path="students/class/:classID" element={<ClassStudents />} />
                <Route path="students/student/:id" element={<ViewStudent />} />
                <Route path="students/student/attendance/:id" element={<StudentAttendance />} />
                <Route path="students/student/marks/:id" element={<StudentExamMarks situation="Student" />} />
                <Route path="students/rollover" element={<SchoolRollover />} />

                <Route path="teachers" element={<ShowTeachers />} />
                <Route path="teachers/management" element={<TeacherManagementOverview />} />
                <Route path="teachers/teacher/:id" element={<TeacherDetails />} />
                <Route path="teachers/chooseclass" element={<ChooseClass situation="Teacher" />} />
                <Route path="teachers/chooseclass/:teacherID" element={<ChooseClass situation="TeacherAssign" />} />
                <Route path="teachers/choosesubject/:id" element={<ChooseSubject situation="Norm" />} />
                <Route path="teachers/choosesubject/:classID/:teacherID" element={<ChooseSubject situation="Teacher" />} />
                <Route path="staff/role" element={<ChooseStaffRole />} />
                <Route path="staff/homeroom/:classID" element={<SetupHomeroomStaff />} />
                <Route path="staff/add" element={<AddStaff />} />
                <Route path="staff/add/:id" element={<AddStaff />} />
                <Route path="teachers/addteacher/:id" element={<AddStaff />} />

                <Route path="schemes" element={<SchemeApprovals />} />
                <Route path="leaves" element={<LeaveApprovals />} />
                <Route path="disciplinary" element={<DisciplinaryApprovals />} />
                <Route path="exams/review" element={<ClassResultsReview />} />
                <Route path="exams/ca" element={<CaAssessmentManagement />} />
                <Route path="exams/records" element={<ResultsRecords />} />
                <Route path="exams/records/:classId" element={<ClassResults />} />
                <Route path="exams/marks-review" element={<SubmissionReview mode="marks" />} />
                <Route path="attendance/review" element={<SubmissionReview mode="attendance" />} />
                <Route path="staff/attendance" element={<StaffAttendanceRegister />} />
                <Route path="enrollment/review" element={<EnrollmentReview />} />
                <Route path="submissions" element={<Navigate to="exams/review" replace />} />

                <Route path="fees/structures" element={<FinanceAdminOnly><FeeStructureManagement /></FinanceAdminOnly>} />
                <Route path="fees/payment-settings" element={<FinanceAdminOnly><SchoolFeePaymentSettings /></FinanceAdminOnly>} />
                <Route path="fees/collection" element={<FinanceAdminOnly><FeeCollection /></FinanceAdminOnly>} />
                <Route path="fees/payment-submissions" element={<FinanceAdminOnly><FeePaymentSubmissions /></FinanceAdminOnly>} />
                <Route path="fees/record/:id" element={<FinanceAdminOnly><FeeRecordDetail /></FinanceAdminOnly>} />
                <Route path="fees/defaulters" element={<FinanceAdminOnly><FeeDefaulters /></FinanceAdminOnly>} />
                <Route path="fees/aids" element={<FinanceAdminOnly><FeeAidManagement /></FinanceAdminOnly>} />
                <Route path="fees/reports" element={<FinanceAdminOnly><FinanceReports /></FinanceAdminOnly>} />

                <Route path="expenses/log" element={<FinanceAdminOnly><ExpenseLog /></FinanceAdminOnly>} />
                <Route path="expenses/detail/:id" element={<FinanceAdminOnly><ExpenseDetail /></FinanceAdminOnly>} />
                <Route path="expenses/budgets" element={<FinanceAdminOnly><TermBudgetManagement /></FinanceAdminOnly>} />
                <Route path="expenses/summary" element={<FinanceAdminOnly><ExpenseSummary /></FinanceAdminOnly>} />

                <Route path="school-page" element={<SchoolPageBuilder />} />

                <Route path="hostels" element={<HostelManagement defaultTab={0} />} />
                <Route path="hostels/boarders" element={<HostelManagement defaultTab={2} />} />

                <Route
                    path="library"
                    element={
                        <LibraryAdminOnly>
                            <LibraryManagement canManageInventory />
                        </LibraryAdminOnly>
                    }
                />
                <Route
                    path="library/overview"
                    element={
                        <LibraryAdminOnly>
                            <AdminTermResourcesOverview />
                        </LibraryAdminOnly>
                    }
                />

                <Route path="infrastructure" element={<InfrastructureManagement />} />
                <Route path="analytics" element={<AdminAnalyticsPage />} />
                <Route path="emis-reports" element={<EmisReportsDashboard />} />
                <Route path="maneb" element={<ManebDashboard />} />
                <Route path="maneb/session/:sessionId" element={<ManebSessionDetail />} />
                <Route path="maneb/certificates" element={<ManebCertificates />} />

                <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Routes>
        </DashboardShell>
                        </SubscriptionGate>
                    </SchoolVerificationGate>
                }
            />
        </Routes>
    );
}

export default AdminDashboard
